import { Injectable } from '@angular/core';

// Standardwerte für die Höhe, auf die das Bild verkleinert wird.
const DEFAULT_SHRINK_HEIGHT = 800;
// Standardwerte für die Breite, auf die das Bild verkleinert wird.
const DEFAULT_SHRINK_WIDTH = 1200;

/**
 * Service zum Verkleinern von Bildern.
 */
@Injectable()
export class ImageShrinkingService {
  // Canvas-Element für die Bildverarbeitung.
  private canvas: HTMLCanvasElement = document.createElement('canvas');
  // 2D-Zeichenkontext des Canvas für die Bildmanipulation.
  private ctx: CanvasRenderingContext2D | null = this.canvas.getContext('2d');

  /**
   * Verkleinert ein Bild auf die angegebenen Maximalwerte.
   * Wenn die Bildgröße bereits kleiner ist als die Maximalwerte, wird das Originalbild zurückgegeben.
   *
   * Standardwerte für die Breite und Höhe sind 1200 Pixel.
   *
   * @param file - Das zu verkleinernde Bild als File-Objekt.
   * @param maxWidth - Die maximale Breite des verkleinerten Bildes.
   * @param maxHeight - Die maximale Höhe des verkleinerten Bildes.
   * @returns Ein Promise, das das verkleinerte Bild als File-Objekt zurückgibt.
   */
  public shrinkImage(
    file: File,
    maxWidth: number = DEFAULT_SHRINK_WIDTH,
    maxHeight: number = DEFAULT_SHRINK_HEIGHT)
    : Promise<File> {
    if (maxWidth <= 0 || maxHeight <= 0) {
      return Promise.reject('[ImageShrinkingService][shrinkImage]: Maximale Breite und Höhe müssen positive Zahlen sein.');
    }

    // Berechnung der maximalen Dateigröße basierend auf der Pixelanzahl und 8 Bit pro Pixel (1 Byte).
    const maxSizeInBytes: number = maxWidth * maxHeight;

    // Wenn die aktuelle Dateigröße kleiner ist als die maximale Größe, wird das Originalbild zurückgegeben.
    if (file.size < maxSizeInBytes) {
      return Promise.resolve(file);
    }

    // Erstellung eines neuen Image-Objekts zur Verarbeitung des Bildes.
    const image = new Image();

    // Erstellung eines Canvas-Elements, auf dem das Bild verkleinert wird.
    const canvas: HTMLCanvasElement = this.canvas;
    const ctx: CanvasRenderingContext2D | null = this.ctx;

    // Rückgabe eines Promises, das den Verkleinerungsprozess kapselt.
    return new Promise((resolve, reject) => {
      // Verwendung einer Arrow Function, um den korrekten 'this'-Kontext zu gewährleisten.
      image.onload = (): void => {
        // Aufruf der Resize-Methode zur Anpassung der Bildgröße.
        this.resize(canvas, image, maxWidth, maxHeight, ctx);

        // Umwandlung des Canvas-Inhalts in ein Blob-Objekt.
        canvas.toBlob(
          (blob: Blob | null) => {
            // Freigabe der erstellten Objekt-URL zur Vermeidung von Speicherlecks.
            URL.revokeObjectURL(image.src);

            if (blob) {
              // Erstellung eines neuen File-Objekts aus dem Blob und Auflösung des Promises.
              resolve(new File([blob], file.name, {type: file.type}));
            } else {
              // Ablehnung des Promises bei Fehlern während der Blob-Erstellung.
              reject('[ImageShrinkingService][shrinkImage]: Fehler beim Konvertieren des Canvas zu Blob');
            }
          },
          file.type, // Beibehaltung des ursprünglichen Dateityps.
          0.9        // Qualitätsfaktor (bei verlustbehafteten Formaten wie JPEG).
        );
      };

      // Fehlerbehandlung für das Laden des Bildes.
      image.onerror = (): void => {
        reject('[ImageShrinkingService][shrinkImage]: Fehler beim Laden des Bildes');
      };

      // Erstellung einer temporären Objekt-URL für das Laden des Bildes.
      image.src = URL.createObjectURL(file);
    });
  }

  /////////////////////////// PRIVATE HILFSMETHODEN ///////////////////////////

  /**
   * Verkleinert das Bild auf die maximal angegebenen Breiten- und Höhenwerte und zeichnet es auf das Canvas.
   *
   * @param canvas - Das Canvas-Element, auf dem das Bild gezeichnet wird.
   * @param image - Das geladene Image-Objekt.
   * @param maxWidth - Die maximale Breite des verkleinerten Bildes.
   * @param maxHeight - Die maximale Höhe des verkleinerten Bildes.
   * @param ctx - Der 2D-Zeichenkontext des Canvas.
   */
  private resize(
    canvas: HTMLCanvasElement,
    image: HTMLImageElement,
    maxWidth: number,
    maxHeight: number,
    ctx: CanvasRenderingContext2D | null
  ): void {
    // Originalbreite und -höhe des Bildes.
    let width = image.width;
    let height = image.height;

    // Berechnung der maximalen Größe unter Beibehaltung des Seitenverhältnisses.
    ({width, height} = this.calculateMaxSize(width, height, maxWidth, maxHeight));

    // Einstellung der Canvas-Größe auf die berechneten Werte.
    canvas.width = width;
    canvas.height = height;

    // Zeichnen des Bildes auf das Canvas mit den neuen Abmessungen.
    ctx?.drawImage(image, 0, 0, width, height);
  }

  /**
   * Berechnet die maximalen Breiten- und Höhenwerte, sodass das Bild die gleichen Proportionen behält.
   *
   * @param width - Originalbreite des Bildes.
   * @param height - Originalhöhe des Bildes.
   * @param maxWidth - Maximale Breite.
   * @param maxHeight - Maximale Höhe.
   * @returns Ein Objekt mit den neuen Breiten- und Höhenwerten.
   */
  private calculateMaxSize(
    width: number,
    height: number,
    maxWidth: number,
    maxHeight: number
  ): { width: number, height: number } {
    // Berechnung des Seitenverhältnisses.
    const aspectRatio: number = width / height;

    if (width > height) {
      // Wenn die Breite größer ist, begrenzen wir die Breite und berechnen die Höhe entsprechend.
      if (width > maxWidth) {
        width = maxWidth;
        height = Math.round(width / aspectRatio);
      }
    } else {
      // Wenn die Höhe größer ist oder gleich, begrenzen wir die Höhe und berechnen die Breite entsprechend.
      if (height > maxHeight) {
        height = maxHeight;
        width = Math.round(height * aspectRatio);
      }
    }

    return {width, height};
  }
}
